/**
 * useLeaderboard Hook
 * Shared between web and mobile for leaderboard data fetching
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, type HttpClient } from '../api/httpClient';

// =============================================================================
// Constants
// =============================================================================

const PAGE_SIZE = 20;

// =============================================================================
// Types
// =============================================================================

export type TimePeriod = '7d' | '30d' | 'season' | 'alltime';
export type BetsType = 'all' | 'actual';
export type SortBy = 'roi' | 'winRate' | 'rawRoi' | 'rawWinRate' | 'profit' | 'streak';

export interface LeaderboardUser {
  id: string;
  displayName: string | null;
  isVerified: boolean;
  followerCount: number;
  followingCount: number;
}

export interface LeaderboardStats {
  // Raw stats (actual user performance)
  roi: number;
  winRate: number;
  totalBets: number;
  wins: number;
  losses: number;
  pushes: number;
  // Profit and stakes are null when the owner does not share 'Stakes and profit' with this
  // viewer; units are null only outside betsType='actual' and for a row with no stakes.
  totalWagered: number | null;
  totalProfit: number | null;
  totalUnits: number | null;
  currentStreak: number;
  avgOdds: number;
  // Bayesian-adjusted stats (used for ranking)
  bayesianRoi?: number;
  bayesianWinRate?: number;
}

export interface LeaderboardEntry {
  rank: number;
  user: LeaderboardUser;
  stats: LeaderboardStats;
  specialty?: {
    propType: string;
    winRate: number;
    totalBets: number;
  };
}

export interface UserRank {
  rank: number | null;
  percentile: number | null;
}

export interface LeaderboardFilters {
  timePeriod: TimePeriod;
  betsType: BetsType;
  propType?: string;
  minBets: number;
  verifiedOnly: boolean;
  sortBy: SortBy;
  limit: number;
  offset: number;
}

export interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
  userRank: UserRank | null;
  totalUsers: number;
  filters: LeaderboardFilters;
}

export interface UseLeaderboardResult {
  data: LeaderboardData | null;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export interface UseLeaderboardOptions {
  /** HTTP client for API calls */
  httpClient: HttpClient;
  timePeriod?: TimePeriod;
  betsType?: BetsType;
  propType?: string;
  minBets?: number;
  verifiedOnly?: boolean;
  followingOnly?: boolean;
  sortBy?: SortBy;
  /** Auto-load on mount (default true) */
  autoLoad?: boolean;
}

// =============================================================================
// Hook
// =============================================================================

export function useLeaderboard(options: UseLeaderboardOptions): UseLeaderboardResult {
  const {
    httpClient,
    timePeriod = '30d',
    betsType = 'all',
    propType,
    minBets = 10,
    verifiedOnly = false,
    followingOnly = false,
    sortBy = 'roi',
    autoLoad = true,
  } = options;

  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const accumulatedRef = useRef<LeaderboardEntry[]>([]);

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      setLoading(true);
      setError(null);

      try {
        const queryParams: Record<string, string | number | boolean> = {
          period: timePeriod,
          betsType,
          minBets,
          verified: verifiedOnly,
          following: followingOnly,
          sortBy,
          limit: PAGE_SIZE,
          offset,
        };

        if (propType) {
          queryParams.propType = propType;
        }

        const responseData = await httpClient.get<LeaderboardData>('/api/leaderboard', queryParams);

        const newEntries = responseData.leaderboard;

        if (append) {
          accumulatedRef.current = [...accumulatedRef.current, ...newEntries];
        } else {
          accumulatedRef.current = newEntries;
        }

        setData({
          ...responseData,
          leaderboard: accumulatedRef.current,
        });
        setHasMore(
          accumulatedRef.current.length < responseData.totalUsers &&
            newEntries.length === PAGE_SIZE,
        );
      } catch (err) {
        const errorMsg = ApiError.getUserMessage(err, 'Failed to load leaderboard');
        setError(errorMsg);
        console.error('Failed to load leaderboard:', err);
      } finally {
        setLoading(false);
      }
    },
    [httpClient, timePeriod, betsType, propType, minBets, verifiedOnly, followingOnly, sortBy],
  );

  // Initial load + reload on filter change
  const reload = useCallback(async () => {
    accumulatedRef.current = [];
    await fetchPage(0, false);
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    await fetchPage(accumulatedRef.current.length, true);
  }, [fetchPage, loading, hasMore]);

  // Load on mount and when filters change
  useEffect(() => {
    if (autoLoad) {
      reload();
    }
  }, [autoLoad, reload]);

  return {
    data,
    loading,
    error,
    hasMore,
    reload,
    loadMore,
  };
}
